// Распознавание речи средствами браузера (Web Speech API). На iPhone это
// использует системный движок (Siri) — локально и бесплатно. Аудио наружу не
// уходит — мы получаем уже готовый текст.
//
// Управление очередью реплик (когда слушать, когда молчать) делает вызывающий
// код: здесь только обёртка над SpeechRecognition с понятными колбэками.

type RecognitionCtor = new () => SpeechRecognitionLike

interface SpeechRecognitionLike {
  lang: string
  continuous: boolean
  interimResults: boolean
  maxAlternatives: number
  start(): void
  stop(): void
  abort(): void
  onresult: ((e: SpeechRecognitionEventLike) => void) | null
  onerror: ((e: { error: string }) => void) | null
  onend: (() => void) | null
  onstart: (() => void) | null
}

interface SpeechRecognitionEventLike {
  resultIndex: number
  results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>
}

export interface SpeechRecognizerOptions {
  lang: string
  onInterim: (text: string) => void
  onFinal: (text: string) => void
  onError?: (error: string) => void
  /** Вызывается, когда распознавание остановилось (manual=true — мы сами стопнули). */
  onEnd?: (manual: boolean) => void
}

export class SpeechRecognizer {
  private rec: SpeechRecognitionLike | null = null
  readonly supported: boolean
  private manualStop = false

  constructor(opts: SpeechRecognizerOptions) {
    const Ctor =
      (window as unknown as { SpeechRecognition?: RecognitionCtor }).SpeechRecognition ||
      (window as unknown as { webkitSpeechRecognition?: RecognitionCtor }).webkitSpeechRecognition
    this.supported = !!Ctor
    if (!Ctor) return

    const rec = new Ctor()
    rec.lang = opts.lang
    rec.interimResults = true
    rec.continuous = false // одна реплика за заход; рестарт — на стороне вызывающего
    rec.maxAlternatives = 1

    rec.onresult = (e) => {
      let interim = ''
      let final = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i]
        const text = res[0]?.transcript ?? ''
        if (res.isFinal) final += text
        else interim += text
      }
      if (final.trim()) opts.onFinal(final.trim())
      else if (interim.trim()) opts.onInterim(interim.trim())
    }
    rec.onerror = (e) => opts.onError?.(e.error)
    rec.onend = () => opts.onEnd?.(this.manualStop)

    this.rec = rec
  }

  start(): void {
    if (!this.rec) return
    this.manualStop = false
    try {
      this.rec.start()
    } catch {
      /* уже запущен — игнорируем */
    }
  }

  stop(): void {
    if (!this.rec) return
    this.manualStop = true
    try {
      this.rec.stop()
    } catch {
      /* noop */
    }
  }

  abort(): void {
    if (!this.rec) return
    this.manualStop = true
    try {
      this.rec.abort()
    } catch {
      /* noop */
    }
  }
}
